import { Injectable, inject } from '@angular/core';
import { Page, PageResult, ServiceHandler } from '@nucleus/ui-base';
import {
  NucleusAccessRequest,
  NucleusAccessRequestFilter,
  NucleusAccessRequestServiceContract,
} from '@nucleus/access-request-ui';

import { AccessRequestService } from '../access-request/access-request.service';
import { UserAccessRequest } from '@nucleus/user-ui';
import { FilterRequest } from '../filter-request';

/**
 * Bridges TaskSense's concrete AccessRequestService to the lib's
 * NucleusAccessRequestServiceContract. search / filter / approve / reject
 * pass straight through — TaskSense already uses the matching names.
 * Reports take a FilterRequest envelope on the TaskSense side, so we wrap
 * (filter, page) before delegating.
 *
 * Wired in @nucleus/access-request-ui's NUCLEUS_ACCESS_REQUEST_SERVICE
 * token via NUCLEUS_ACCESS_REQUEST_UI_PROVIDERS in the index barrel.
 */
@Injectable({ providedIn: 'root' })
export class TasksenseAccessRequestServiceAdapter implements NucleusAccessRequestServiceContract {
  private readonly delegate = inject(AccessRequestService);

  search(handler: ServiceHandler<NucleusAccessRequest[]>, _clientId?: string): void {
    this.delegate.search(handler as unknown as ServiceHandler<UserAccessRequest[]>);
  }

  filter(
    handler: ServiceHandler<PageResult<NucleusAccessRequest>>,
    filter: NucleusAccessRequestFilter,
    page?: Page,
    _clientId?: string,
  ): void {
    this.delegate.filter(
      handler as unknown as ServiceHandler<PageResult<UserAccessRequest>>,
      filter,
      page,
    );
  }

  approveAccessRequest(id: number, handler: ServiceHandler<void>): void {
    this.delegate.approveAccessRequest(id, handler);
  }

  rejectAccessRequest(id: number, handler: ServiceHandler<void>): void {
    this.delegate.rejectAccessRequest(id, handler);
  }

  getPDFReport(handler: ServiceHandler<Blob>, filter: NucleusAccessRequestFilter, page?: Page): void {
    this.delegate.getPDFReport(handler, this.toRequest(filter, page));
  }

  getXLSReport(handler: ServiceHandler<Blob>, filter: NucleusAccessRequestFilter, page?: Page): void {
    this.delegate.getXLSReport(handler, this.toRequest(filter, page));
  }

  getCSVReport(handler: ServiceHandler<Blob>, filter: NucleusAccessRequestFilter, page?: Page): void {
    this.delegate.getCSVReport(handler, this.toRequest(filter, page));
  }

  private toRequest(filter: NucleusAccessRequestFilter, page?: Page): FilterRequest<NucleusAccessRequestFilter> {
    const req = new FilterRequest<NucleusAccessRequestFilter>(filter);
    if (page) req.page = page;
    return req;
  }
}
